import {useMemo} from 'react';
import {useParams} from 'react-router';
import {Moment} from 'moment';
import {useTranslation} from 'react-i18next';
import {translations} from '../../../../i18n/translations';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import {DateRangeOnChange} from '../../../date-range-input/date-range-utils';
import {ScreenPaths} from '../../../../enums/screen-path';
import {CareServicesHeader} from '../../base/header/care-services-header';
import {
  CareServicePreparedExportData,
  CareServiceCreatingItem,
  OnCareServiceExportPdfClick,
} from '../../base/data/care-service-item';
import {FetchEntityUsersResult} from '../../../../hooks/use-fetch-entity-users';
import {useAuth} from '../../../../contexts/auth/context';

interface SeniorsCareServicesHeaderProps {
  startDate: Moment;
  endDate: Moment;
  filterUsers: FetchEntityUsersResult;
  canWrite?: boolean;
  onDateChange: DateRangeOnChange;
  onCreateItemClick: (newItem: CareServiceCreatingItem) => void;
  onFilterUserChange: (user: AffiliatedUser | undefined) => void;
  onExportXlsxClick?: (preparedData: CareServicePreparedExportData) => void;
  onExportPdfClick?: OnCareServiceExportPdfClick;
}

export function SeniorsCareServicesHeader(props: SeniorsCareServicesHeaderProps) {
  let {entityId}: {url: string; entityId: string; id: string} = useParams();
  const {t} = useTranslation();
  const authContext = useAuth();
  const entity = authContext.state.session?.selectedAffiliation?.entity;

  const crumbs = useMemo(() => {
    return [
      {
        label: t(translations.screen_paths.professional_caregiver_team),
        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}`,
      },
      {
        label: t(translations.teamSideMenu.careServices),
        link: `${ScreenPaths.Office}${ScreenPaths.Office_ProfessionalCaregiver}/${entityId}${ScreenPaths.ProfessionalCaregiver_ProfessionalCaregivers}${ScreenPaths.ProfessionalCaregiver_CareServices}`,
      },
    ];
  }, [entityId, t]);

  return (
    <CareServicesHeader
      isLoading={false}
      crumbs={crumbs}
      startDate={props.startDate}
      endDate={props.endDate}
      canWrite={props.canWrite}
      onDateChange={props.onDateChange}
      onCreateItemClick={(item) => {
        // --- additional fields ---
        // nothing to add here

        props.onCreateItemClick(item);
      }}
      filterUsers={props.filterUsers}
      filterDefaultOptionLabel={translations.care_services.header.filter_caregivers_label}
      onFilterUserChange={props.onFilterUserChange}
      onExportXlsxClick={
        props.onExportXlsxClick
          ? () => {
              props.onExportXlsxClick?.({entity});
            }
          : undefined
      }
      onExportPdfClick={
        props.onExportPdfClick
          ? () => {
              props.onExportPdfClick?.({
                startDate: props.startDate,
                endDate: props.endDate,
                entity,
              });
            }
          : undefined
      }
    />
  );
}
